/**
 * Named gesture-tuning presets.
 *
 * These were previously magic numbers scattered across three files with subtly
 * different values (deadzone 8, rubber-band 0.25 vs 0.3, fling 300/50/2 vs
 * 300/40/2…). Centralising them keeps the feel consistent and makes tuning a
 * one-line change. Each preset bundles the recognizer's `fling`/`thresholdPx`
 * with the consumer-facing layout constants (commit thresholds, rubber-band
 * limits, animation durations) that the recognizer itself does not own.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/**
 * Per-row swipe-to-action in the notes sidebar (swipe a page row left to
 * delete). Mirrors listto's `TODO_ROW_SWIPE` feel so swipe gestures across the
 * product family stay consistent; rows are shorter than todo cards, so the
 * commit threshold and rubber-band limit are tuned a touch tighter.
 */
export const PAGE_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 96,
  rubberBandLimitPx: 120,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 220,
  cancelAnimationMs: 220,
  fallbackWidthPx: 240,
} as const;

/**
 * Horizontal carousel between sibling notes in the detail view (swipe
 * left/right navigates to the previous/next page in the flat tree list). Commit by ratio
 * (canonical `DRAWER_CAROUSEL` style): confirms at whichever is smaller, a fixed
 * distance or a fraction of the width, or on a fling. The detail remounts by route, so we only
 * animate the current panel's exit before navigating.
 */
export const NOTE_CAROUSEL = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.22,
  rubberBandFactor: 0.3,
  transitionMs: 240,
  fallbackWidthPx: 320,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Dismissal by vertically dragging sheets/dialogs/popovers (dragging down
 * dismisses). Y-axis only, downward only, and only when inner scroll is
 * at the top. Mirrors the vertical half of the canonical
 * `DRAWER_CAROUSEL`.
 */
export const SHEET_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Actual travel (after rubber-banding) required to confirm dismissal. */
  commitThresholdPx: 100,
  /** Beyond this point, the "drag to dismiss" indicator is shown. */
  indicatorTriggerPx: 15,
  rubberBandFactor: 0.5,
  transitionMs: 320,
  fallbackHeightPx: 800,
  hapticMs: 12,
  /** Vertical fling: less travel than horizontal. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;

/**
 * Swipe-to-dismiss for toast notifications. Single-axis (X) so it never fights
 * vertical scroll; the card follows the finger in either direction and is
 * dismissed past a fraction of its width or on a fling, otherwise snaps back.
 * Mirrors the canon `TOAST_SWIPE` feel shared across the product family: free
 * tracking (no rubber-band clamp) since the card leaves the screen on commit.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.4,
  rubberBandLimitPx: 9999,
  rubberBandFactor: 1,
  hapticMs: 12,
  commitAnimationMs: 220,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;

/** Page-level swipe (X) and pull-to-dashboard (Y). */
export const PAGE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  indicatorTriggerPx: 10,
  commitThresholdPx: 80,
  rubberBandFactor: 0.3,
  transitionMs: 240,
  fallbackWidthPx: 320,
  fallbackHeightPx: 800,
  fling: { ...STANDARD_FLING },
  /** Vertical fling needs less travel than horizontal. */
  flingMinYDistancePx: 15,
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;
